import {FC, useEffect, useState} from 'react';
import DashboardLayout from "@/components/layouts/Dashboard";
import Card from "@/components/Card";
import {
  apiGetProducts,
  apiGetSubscriptionCheckoutSession,
  apiGetPaymentCheckoutSession,
  ProductItemResponse,
  ProductsResponse
} from '@/services/ProductSubscriptionService';
import {Button} from "@/components/ui/Button/index";
import {
  AlertCircle,
  CheckIcon,
  CreditCard,
  Zap,
  Crown,
  Star,
  Search,
  Rocket,
  Code,
  BarChart3,
  Clock,
  Shield,
  Headphones,
  TrendingUp
} from "lucide-react";
import Alert from "@/components/ui/Alert/index";
import {Link} from "react-router-dom";

interface PricingPlansProps {
}

const PricingPlans: FC<PricingPlansProps> = () => {
  const [productsResponse, setProductsResponse] = useState<ProductsResponse | null>(null);
  const [intervals, setIntervals] = useState<string[]>([]);
  const [activeInterval, setActiveInterval] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [filteredProducts, setFilteredProducts] = useState<ProductItemResponse[]>([]);

  // Smooth scroll to module explanation with header offset
  const scrollToModuleExplanation = () => {
    // Try subscription section first, then one-time purchases section
    const element = document.getElementById('module-explanation') || 
                   document.getElementById('module-explanation-onetime');
    if (element) {
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - 120; // 120px offset for dashboard header
      
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  // When active interval changes, filter products accordingly
  useEffect(() => {
    if (productsResponse?.products && activeInterval) {
      filterProductsByInterval(activeInterval);
    }
  }, [activeInterval, productsResponse]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const response = await apiGetProducts<ProductsResponse>();

      if (response) {
        setProductsResponse(response.data);

        // Check if we received subscription products
        if (response.data.productType === 'SUBSCRIPTION') {
          const products = response.data.products;

          // Collect all unique intervals
          const allIntervals = new Set<string>();
          products.forEach(product => {
            if (product.interval) {
              allIntervals.add(product.interval);
            }
          });

          // Convert intervals set to array and sort
          const intervalsList = [...allIntervals];
          intervalsList.sort((a, b) => {
            // Sort monthly before yearly
            if (a.toLowerCase().includes('month') && b.toLowerCase().includes('year')) return -1;
            if (a.toLowerCase().includes('year') && b.toLowerCase().includes('month')) return 1;
            return a.localeCompare(b);
          });

          setIntervals(intervalsList);

          // Set default active interval to the first one
          if (intervalsList.length > 0) {
            setActiveInterval(intervalsList[0]);
          }
        }
      }
    } catch (err: any) {
      setError('Failed to load products. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const filterProductsByInterval = (interval: string) => {
    if (!productsResponse?.products) return;

    // Filter products by the selected interval
    const filtered = productsResponse.products.filter(
        product => product.interval === interval
    );

    setFilteredProducts(filtered);
  };

  const formatCurrency = (amount: number, currency: string): string => {
    return new Intl.NumberFormat('fr-Fr', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 1,
      maximumFractionDigits: 2,
    }).format(amount / 100); // Assuming amount is in cents
  };

  const [selectingProduct, setSelectingProduct] = useState<string | null>(null);

  const handleSelectProduct = async (product: ProductItemResponse) => {
    try {
      setSelectingProduct(product.priceId);

      let response;
      if (productsResponse?.productType === 'SUBSCRIPTION') {
        response = await apiGetSubscriptionCheckoutSession(product.priceId);
      } else {
        response = await apiGetPaymentCheckoutSession(product.priceId);
      }

      if (response && response.data) {
        // First check if it's a string
        if (typeof response.data === 'string') {
          window.location.href = response.data;
        }
        // Then check if it's an object before using 'in' operator
        else if (typeof response.data === 'object' && response.data !== null) {
          // Now safely check for the 'url' property
          if ('url' in response.data) {
            window.location.href = (response.data as { url: string }).url;
          } else {
            setError('Unable to create checkout session. Please try again later.');
          }
        } else {
          setError('Unable to create checkout session. Please try again later.');
        }
      } else {
        setError('Unable to create checkout session. Please try again later.');
      }
    } catch (err: any) {
      setError('Unable to create checkout session. Please try again later.');
    } finally {
      setSelectingProduct(null);
    }
  };

  // Helper function to get package info based on product
  const getPackageInfo = (product: ProductItemResponse) => {
    // Map product names to package info
    const planName = product.name.toLowerCase();

    if (planName.includes('startup')) {
      return {
        name: "Startup Pack",
        tagline: "Try It. Love It.",
        description: "Dip your toes in—generate your first AI module with zero commitment. Perfect for evaluating code and architecture quality before scaling up.",
        icon: Rocket,
        color: "green",
        features: [
          {icon: Zap, text: `${product.credits.toLocaleString('fr-FR')} credits`, highlight: false, clickable: true},
          {icon: Code, text: "Instant access to all code-generation models", highlight: false},
          {icon: BarChart3, text: "Web dashboard with detailed credit-usage tracking", highlight: false},
          {icon: Headphones, text: "Email support for quick questions", highlight: false}
        ],
        advantages: [
          "Risk-free trial: see real results before you buy more",
          "Fast onboarding: onboard in minutes"
        ]
      };
    } else if (planName.includes('business')) {
      return {
        name: "Business Pack",
        tagline: "Build & Scale Fast.",
        description: "Kick off your first project with enough credits to generate a service. Ideal to get your first Production-Ready Microservice in a few hours",
        icon: Code,
        color: "purple",
        features: [
          {icon: Zap, text: `${product.credits.toLocaleString('fr-FR')} credits`, highlight: false, clickable: true},
          {icon: Crown, text: "Premium Fast AI servers", highlight: true},
          {icon: Clock, text: "Priority queue for faster turnaround", highlight: false},
          {icon: TrendingUp, text: "Access to all model improvements as they roll out", highlight: false},
          {icon: Shield, text: "Email support with guaranteed 4-hour SLAs", highlight: true}
        ],
        advantages: [
          "Affordable ramp-up—test multiple use-cases without over-commitment",
          "Faster feedback loops: get quickly a working service",
          "Test Pull Requests and integration tests and explore all the power of ShredsAI"
        ]
      };
    } else if (planName.includes('enterprise')) {
      return {
        name: "Enterprise Pack",
        tagline: "Ultimate Scale.",
        description: "Get serious about Production-Ready AI development with a package designed for high-velocity teams. Perfect for building multiple microservices in parallel.",
        icon: Crown,
        color: "blue",
        features: [
          {icon: Zap, text: `${product.credits.toLocaleString('fr-FR')} credits`, highlight: false, clickable: true},
          {icon: Crown, text: "Premium Fast AI servers", highlight: true},
          {icon: BarChart3, text: "Priority Pull Requests Processing", highlight: true},
          {icon: Shield, text: "Dedicated support with custom SLAs", highlight: true},
          {icon: TrendingUp, text: "All model improvements", highlight: false}
        ],
        advantages: [
          "Perfect to start working on a full project",
          "High-velocity dev cycles: fastest servers and priority processing",
          "Quick support to resolve any issue and answer questions"
        ]
      };
    }

    // Default fallback
    return {
      name: product.name,
      tagline: "Get Started.",
      description: product.description,
      icon: Search,
      color: "blue",
      features: [
        {icon: Zap, text: `${product.credits.toLocaleString('fr-FR')} credits`, highlight: false, clickable: true},
        {icon: Code, text: "All code-generation models", highlight: false},
        {icon: BarChart3, text: "Credit usage tracking", highlight: false},
        {icon: Headphones, text: "Email support", highlight: false}
      ],
      advantages: [
        "Full access to platform features",
        "Standard support included"
      ]
    };
  };

  // Render different UI based on product type
  const renderContent = () => {
    if (!productsResponse) return null;

    if (productsResponse.productType === 'SUBSCRIPTION') {
      return renderSubscriptionPlans();
    } else if (productsResponse.productType === 'ONE_TIME') {
      return renderOneTimePurchases();
    } else if (productsResponse.productType === 'DUE') {
      return renderPaymentDue();
    }
  };

  // New function to render Payment Due message
  const renderPaymentDue = () => {
    return (
        <div className="py-12 flex flex-col items-center justify-center text-center">
          <div className="rounded-full bg-red-100 p-4 mb-6">
            <AlertCircle className="h-12 w-12 text-red-600"/>
          </div>

          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            Payment Issue Detected
          </h2>

          <p className="text-gray-600 max-w-2xl mx-auto mb-6">
            Your subscription payment is past due. You need to update your payment method to continue using our services
            and purchase additional credits.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 mt-4">
            <Link to="/settings">
              <Button
                  variant="solid"
                  className="bg-indigo-600 hover:bg-indigo-700 text-white flex items-center justify-center"
                  icon={<CreditCard className="h-5 w-5 mr-2"/>}
              >
                Update Payment Method
              </Button>
            </Link>
          </div>

          <div className="mt-8 p-4 bg-yellow-50 border border-yellow-100 rounded-md max-w-2xl">
            <p className="text-sm text-yellow-700">
              <strong>Note:</strong> Once your payment is updated, you'll regain access to purchase additional credits
              and continue using all features.
            </p>
          </div>
        </div>
    );
  };

  // Enhanced subscription plans with new package structure
  const renderSubscriptionPlans = () => {
    // Sort products by price to ensure proper tier assignment
    const sortedProducts = [...filteredProducts].sort((a, b) => a.amount - b.amount);

    // Rearrange to put popular in the middle if exists
    const arrangedProducts = [];
    const popularIndex = sortedProducts.findIndex(product => product.popular);
    const nonPopularProducts = sortedProducts.filter(product => !product.popular);

    if (popularIndex !== -1 && nonPopularProducts.length > 0) {
      // Place first non-popular on left
      if (nonPopularProducts[0]) arrangedProducts.push(nonPopularProducts[0]);
      // Place popular in middle
      arrangedProducts.push(sortedProducts[popularIndex]);
      // Place remaining non-popular on right
      if (nonPopularProducts[1]) arrangedProducts.push(nonPopularProducts[1]);
      // Add any remaining products
      nonPopularProducts.slice(2).forEach(product => arrangedProducts.push(product));
    } else {
      // If no popular or no non-popular, use sorted order
      arrangedProducts.push(...sortedProducts);
    }

    return (
        <div className="relative">
          {/* Animated background elements */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div
                className="absolute top-20 left-10 w-32 h-32 bg-gradient-to-br from-blue-400/10 to-purple-400/10 rounded-full blur-3xl animate-pulse"></div>
            <div
                className="absolute top-40 right-20 w-24 h-24 bg-gradient-to-br from-green-400/10 to-blue-400/10 rounded-full blur-2xl animate-pulse delay-1000"></div>
            <div
                className="absolute bottom-20 left-1/2 w-40 h-40 bg-gradient-to-br from-purple-400/10 to-pink-400/10 rounded-full blur-3xl animate-pulse delay-500"></div>
          </div>

          <div className="text-center mb-16 relative z-10">
            {/* Shreds symbol header */}
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 rounded-2xl mb-6 shadow-xl relative">
              <img
                  src="/images/shreds-symbol.png"
                  alt="Shreds Symbol"
                  className="w-10 h-10 filter brightness-0 invert"
              />
            </div>

            <h1 className="text-3xl font-bold mb-4 bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
              Access the First Production-Ready Code-Generation AI
            </h1>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto mb-4">
              Build fully tested, runnable backend code for complex projects in just hours, and save up to 99% on development cost and time.
            </p>

            {/* Single interval display as header when only one interval exists */}
            {intervals.length === 1 && (
                <div className="mt-8">
                  <span
                      className="inline-block px-6 py-2 bg-gradient-to-r from-indigo-500/20 to-purple-500/20 text-indigo-800 rounded-full text-sm font-medium border border-indigo-300 backdrop-blur-sm">
                    {intervals[0].charAt(0).toUpperCase() + intervals[0].slice(1)} Billing
                  </span>
                </div>
            )}
          </div>

          {/* Enhanced Interval Selector */}
          {intervals.length > 1 && (
              <div className="flex justify-center mb-16 relative z-10">
                <div
                    className="relative inline-flex rounded-2xl shadow-xl p-1.5 bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 backdrop-blur-sm">
                  {intervals.map((interval) => (
                      <button
                          key={interval}
                          type="button"
                          className={`relative px-8 py-3 text-sm font-semibold rounded-xl transition-all duration-300 ${
                              activeInterval === interval
                                  ? 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-lg transform scale-105'
                                  : 'text-gray-700 hover:bg-white/60 hover:text-indigo-600'
                          }`}
                          onClick={() => setActiveInterval(interval)}
                      >
                        {interval.charAt(0).toUpperCase() + interval.slice(1)}
                        {activeInterval === interval && (
                            <div
                                className="absolute inset-0 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-xl blur opacity-30 -z-10"></div>
                        )}
                      </button>
                  ))}
                </div>
              </div>
          )}

          {/* Enhanced Plans Grid */}
          {arrangedProducts.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto relative z-10">
                {arrangedProducts.map((plan) => {
                  const packageInfo = getPackageInfo(plan);
                  const IconComponent = packageInfo.icon;

                  const colorClasses = {
                    blue: {
                      gradient: 'from-blue-500 to-blue-600',
                      bg: 'from-blue-50 to-blue-100',
                      text: 'text-blue-700',
                      border: 'border-blue-200',
                      icon: 'bg-blue-500'
                    },
                    green: {
                      gradient: 'from-green-500 to-green-600',
                      bg: 'from-green-50 to-green-100',
                      text: 'text-green-700',
                      border: 'border-green-200',
                      icon: 'bg-green-500'
                    },
                    purple: {
                      gradient: 'from-purple-500 to-purple-600',
                      bg: 'from-purple-50 to-purple-100',
                      text: 'text-purple-700',
                      border: 'border-purple-200',
                      icon: 'bg-purple-500'
                    }
                  };

                  const colors = colorClasses[packageInfo.color as keyof typeof colorClasses] || colorClasses.blue;

                  return (
                      <div
                          key={plan.id}
                          className={`relative group ${
                              plan.popular ? 'transform scale-105 z-20' : ''
                          }`}
                      >
                        {/* Popular glow effect */}
                        {plan.popular && (
                            <div
                                className="absolute -inset-1 bg-gradient-to-br from-yellow-400 via-amber-500 to-orange-500 rounded-3xl blur opacity-75 group-hover:opacity-100 transition duration-300"></div>
                        )}

                        <div
                            className={`relative rounded-3xl ${
                                plan.popular
                                    ? 'bg-gradient-to-br from-yellow-50 via-amber-50 to-orange-50 border-0 shadow-2xl'
                                    : `bg-gradient-to-br ${colors.bg} border ${colors.border} shadow-lg hover:shadow-xl`
                            } p-8 flex flex-col h-full transition-all duration-300 hover:transform hover:scale-[1.02]`}
                        >
                          {/* Popular Badge */}
                          {plan.popular && (
                              <>
                                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                                  <div
                                      className="bg-gradient-to-r from-yellow-500 via-amber-500 to-orange-500 text-white text-sm font-bold px-6 py-2 rounded-full shadow-lg flex items-center space-x-2">
                                    <Star className="w-4 h-4"/>
                                    <span>Most Popular</span>
                                  </div>
                                </div>
                                <div
                                    className="absolute top-6 right-6 w-3 h-3 bg-yellow-400 rounded-full animate-ping"></div>
                              </>
                          )}

                          {/* Package Header */}
                          <div className="text-center mb-6">
                            <div
                                className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-4 shadow-lg ${
                                    plan.popular
                                        ? 'bg-gradient-to-br from-yellow-500 via-amber-500 to-orange-500'
                                        : colors.icon
                                }`}>
                              <IconComponent className="w-8 h-8 text-white"/>
                            </div>
                            <h3 className={`text-2xl font-bold mb-1 ${
                                plan.popular
                                    ? 'bg-gradient-to-r from-yellow-600 via-amber-600 to-orange-600 bg-clip-text text-transparent'
                                    : colors.text
                            }`}>
                              {packageInfo.name}
                            </h3>
                            <p className={`text-sm font-medium mb-3 ${
                                plan.popular ? 'text-amber-700' : colors.text
                            }`}>
                              {packageInfo.tagline}
                            </p>
                            <p className="text-gray-600 text-sm leading-relaxed">
                              {packageInfo.description}
                            </p>
                          </div>

                          {/* Pricing */}
                          <div className="text-center mb-6">
                            <div className="flex items-baseline justify-center mb-2">
                              <span className={`text-4xl font-bold ${
                                  plan.popular
                                      ? 'bg-gradient-to-r from-yellow-600 via-amber-600 to-orange-600 bg-clip-text text-transparent'
                                      : colors.text
                              }`}>
                                {formatCurrency(plan.amount, plan.currency)}
                              </span>
                              <span className="text-gray-500 ml-2">/{plan.interval}</span>
                            </div>
                            <div className="text-sm text-gray-500">
                              Auto-renewable • Cancel anytime
                            </div>
                          </div>

                          {/* Features */}
                          <div className="flex-grow mb-6">
                            <div className="space-y-3 mb-6">
                              {packageInfo.features.map((feature, index) => (
                                  <div key={index} className="flex items-start space-x-3">
                                    <div
                                        className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
                                            feature.highlight && !plan.popular
                                                ? colors.icon
                                                : plan.popular
                                                    ? 'bg-gradient-to-br from-yellow-500 via-amber-500 to-orange-500'
                                                    : 'bg-gray-400'
                                        }`}>
                                      <feature.icon className="w-3 h-3 text-white"/>
                                    </div>
                                    {(feature as any).clickable ? (
                                      <button
                                        onClick={scrollToModuleExplanation}
                                        className={`text-sm text-left ${
                                          feature.highlight
                                              ? plan.popular ? 'text-amber-700 font-medium' : `${colors.text} font-medium`
                                              : 'text-gray-700'
                                        } hover:text-indigo-600 transition-colors duration-200 underline decoration-dotted underline-offset-2`}
                                      >
                                        {feature.text}
                                      </button>
                                    ) : (
                                      <span className={`text-sm ${
                                          feature.highlight
                                              ? plan.popular ? 'text-amber-700 font-medium' : `${colors.text} font-medium`
                                              : 'text-gray-700'
                                      }`}>
                                        {feature.text}
                                      </span>
                                    )}
                                  </div>
                              ))}
                            </div>

                            {/* Advantages */}
                            <div className={`p-4 rounded-xl ${
                                plan.popular
                                    ? 'bg-amber-50 border border-amber-200'
                                    : `bg-white/50 border ${colors.border}`
                            }`}>
                              <h4 className={`text-sm font-semibold mb-2 ${
                                  plan.popular ? 'text-amber-800' : colors.text
                              }`}>
                                Key Advantages:
                              </h4>
                              <ul className="space-y-1">
                                {packageInfo.advantages.map((advantage, index) => (
                                    <li key={index} className="text-xs text-gray-600 flex items-start">
                                      <span className="mr-2">•</span>
                                      <span>{advantage}</span>
                                    </li>
                                ))}
                              </ul>
                            </div>
                          </div>

                          {/* CTA Button */}
                          <div className="relative">
                            {plan.popular ? (
                                <div className="relative group">
                                  <div
                                      className="absolute -inset-0.5 bg-gradient-to-r from-yellow-500 via-amber-500 to-orange-500 rounded-2xl blur opacity-75 group-hover:opacity-100 transition duration-300"></div>
                                  <Button
                                      variant="solid"
                                      className="relative w-full bg-gradient-to-r from-yellow-500 via-amber-500 to-orange-500 hover:from-yellow-600 hover:via-amber-600 hover:to-orange-600 text-white py-4 text-lg font-semibold rounded-2xl shadow-lg transform transition-all duration-200 hover:scale-[1.02] flex items-center justify-center"
                                      onClick={() => handleSelectProduct(plan)}
                                      loading={selectingProduct === plan.priceId}
                                  >
                                    {selectingProduct === plan.priceId ? (
                                        <div className="flex items-center justify-center space-x-2">
                                          <div
                                              className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                          <span>Processing...</span>
                                        </div>
                                    ) : (
                                        <div className="flex items-center justify-center space-x-2">
                                          <Crown className="w-5 h-5"/>
                                          <span>Get Started</span>
                                        </div>
                                    )}
                                  </Button>
                                </div>
                            ) : (
                                <Button
                                    variant="solid"
                                    className={`w-full bg-gradient-to-r ${colors.gradient} hover:opacity-90 text-white py-4 text-lg font-semibold rounded-2xl transition-all duration-200 hover:scale-[1.02] flex items-center justify-center`}
                                    onClick={() => handleSelectProduct(plan)}
                                    loading={selectingProduct === plan.priceId}
                                >
                                  {selectingProduct === plan.priceId ? 'Processing...' : 'Choose Plan'}
                                </Button>
                            )}
                          </div>
                        </div>
                      </div>
                  );
                })}
              </div>
          ) : (
              <div className="text-center py-20 relative z-10">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-2xl mb-4">
                  <AlertCircle className="w-8 h-8 text-gray-400"/>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">No Plans Available</h3>
                <p className="text-gray-600">
                  No subscription plans are currently available for {activeInterval} billing.
                </p>
              </div>
          )}

          {/* Module Explanation Section - Below Pricing */}
          <div id="module-explanation" className="max-w-4xl mx-auto mt-20 p-8 bg-gradient-to-r from-indigo-50 via-purple-50 to-blue-50 rounded-3xl border border-indigo-200 backdrop-blur-sm relative z-10">
            <div className="flex items-start space-x-6">
              <div className="flex-shrink-0">
                <div className="w-16 h-16 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-2xl flex items-center justify-center shadow-lg">
                  <Code className="w-8 h-8 text-white" />
                </div>
              </div>
              <div className="flex-1">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">What is a Module?</h3>
                <p className="text-gray-700 mb-6 text-lg leading-relaxed">
                  Each microservice is divided into <strong>modules</strong> - self-contained functional units that handle specific business logic. 
                  A typical microservice is composed of <strong>2 modules on average</strong>, but can range from 1 to several modules depending on complexity.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="flex items-center space-x-3">
                    <div className="w-3 h-3 bg-indigo-500 rounded-full"></div>
                    <span className="text-gray-700 font-medium">1 Module = 1 specific business domain</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-3 h-3 bg-purple-500 rounded-full"></div>
                    <span className="text-gray-700 font-medium">Complete with tests, API endpoints, and database models</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                    <span className="text-gray-700 font-medium">Ready for production deployment</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-3 h-3 bg-indigo-600 rounded-full"></div>
                    <span className="text-gray-700 font-medium">Fully documented and maintained</span>
                  </div>
                </div>
                
                <div className="mt-6 p-4 bg-white/70 rounded-xl border border-indigo-100">
                  <p className="text-gray-600 text-sm">
                    <strong>Example:</strong> An e-commerce microservice might have a "Product Management" module and an "Inventory Tracking" module, 
                    each handling their respective business logic with complete test coverage and API endpoints.
                  </p>
                </div>
              </div>
            </div>
          </div>

        </div>
    );
  };

  // Enhanced one-time purchase products with package structure
  const renderOneTimePurchases = () => {
    const products = productsResponse?.products || [];
    const sortedProducts = [...products].sort((a, b) => a.amount - b.amount);

    // Rearrange to put popular in center for one-time purchases too
    const arrangedProducts = [];
    const popularIndex = sortedProducts.findIndex(product => product.popular);
    const nonPopularProducts = sortedProducts.filter(product => !product.popular);

    if (popularIndex !== -1 && nonPopularProducts.length > 0) {
      if (nonPopularProducts[0]) arrangedProducts.push(nonPopularProducts[0]);
      arrangedProducts.push(sortedProducts[popularIndex]);
      if (nonPopularProducts[1]) arrangedProducts.push(nonPopularProducts[1]);
      nonPopularProducts.slice(2).forEach(product => arrangedProducts.push(product));
    } else {
      arrangedProducts.push(...sortedProducts);
    }

    return (
        <div className="relative">
          {/* Animated background elements */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div
                className="absolute top-20 left-10 w-32 h-32 bg-gradient-to-br from-blue-400/10 to-purple-400/10 rounded-full blur-3xl animate-pulse"></div>
            <div
                className="absolute top-40 right-20 w-24 h-24 bg-gradient-to-br from-green-400/10 to-blue-400/10 rounded-full blur-2xl animate-pulse delay-1000"></div>
          </div>

          <div className="text-center mb-16 relative z-10">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 rounded-2xl mb-6 shadow-xl relative">
              <img
                  src="/images/shreds-symbol.png"
                  alt="Shreds Symbol"
                  className="w-10 h-10 filter brightness-0 invert"
              />
            </div>

            <h1 className="text-5xl font-bold mb-4 bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
              Boost Your Credits
            </h1>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto">
              Purchase additional AI credits to unlock more powerful code generation capabilities and scale your
              development workflow.
            </p>
          </div>

          {arrangedProducts.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto relative z-10">
                {arrangedProducts.map((product) => {
                  const packageInfo = getPackageInfo(product);
                  const IconComponent = packageInfo.icon;

                  const colorClasses = {
                    blue: {
                      gradient: 'from-blue-500 to-blue-600',
                      bg: 'from-blue-50 to-blue-100',
                      text: 'text-blue-700',
                      border: 'border-blue-200',
                      icon: 'bg-blue-500'
                    },
                    green: {
                      gradient: 'from-green-500 to-green-600',
                      bg: 'from-green-50 to-green-100',
                      text: 'text-green-700',
                      border: 'border-green-200',
                      icon: 'bg-green-500'
                    },
                    purple: {
                      gradient: 'from-purple-500 to-purple-600',
                      bg: 'from-purple-50 to-purple-100',
                      text: 'text-purple-700',
                      border: 'border-purple-200',
                      icon: 'bg-purple-500'
                    }
                  };

                  const colors = colorClasses[packageInfo.color as keyof typeof colorClasses] || colorClasses.blue;

                  return (
                      <div
                          key={product.id}
                          className={`relative group ${
                              product.popular ? 'transform scale-105 z-20' : ''
                          }`}
                      >
                        {product.popular && (
                            <div
                                className="absolute -inset-1 bg-gradient-to-br from-yellow-400 via-amber-500 to-orange-500 rounded-3xl blur opacity-75 group-hover:opacity-100 transition duration-300"></div>
                        )}

                        <div
                            className={`relative rounded-3xl ${
                                product.popular
                                    ? 'bg-gradient-to-br from-yellow-50 via-amber-50 to-orange-50 border-0 shadow-2xl'
                                    : `bg-gradient-to-br ${colors.bg} border ${colors.border} shadow-lg hover:shadow-xl`
                            } p-8 flex flex-col h-full transition-all duration-300 hover:transform hover:scale-[1.02]`}
                        >
                          {product.popular && (
                              <>
                                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                                  <div
                                      className="bg-gradient-to-r from-yellow-500 via-amber-500 to-orange-500 text-white text-sm font-bold px-6 py-2 rounded-full shadow-lg flex items-center space-x-2">
                                    <Star className="w-4 h-4"/>
                                    <span>Best Value</span>
                                  </div>
                                </div>
                                <div
                                    className="absolute top-6 right-6 w-3 h-3 bg-yellow-400 rounded-full animate-ping"></div>
                              </>
                          )}

                          <div className="text-center mb-6">
                            <div
                                className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-4 shadow-lg ${
                                    product.popular
                                        ? 'bg-gradient-to-br from-yellow-500 via-amber-500 to-orange-500'
                                        : colors.icon
                                }`}>
                              <IconComponent className="w-8 h-8 text-white"/>
                            </div>
                            <h3 className={`text-2xl font-bold mb-1 ${
                                product.popular
                                    ? 'bg-gradient-to-r from-yellow-600 via-amber-600 to-orange-600 bg-clip-text text-transparent'
                                    : colors.text
                            }`}>
                              {packageInfo.name}
                            </h3>
                            <p className={`text-sm font-medium mb-3 ${
                                product.popular ? 'text-amber-700' : colors.text
                            }`}>
                              {packageInfo.tagline}
                            </p>
                            <p className="text-gray-600 text-sm">{packageInfo.description}</p>
                          </div>

                          <div className="text-center mb-6">
                            <span className={`text-4xl font-bold ${
                                product.popular
                                    ? 'bg-gradient-to-r from-yellow-600 via-amber-600 to-orange-600 bg-clip-text text-transparent'
                                    : colors.text
                            }`}>
                              {formatCurrency(product.amount, product.currency)}
                            </span>
                            <div className="text-sm text-gray-500 mt-2">One-time purchase</div>
                          </div>

                          <div className="flex-grow mb-6">
                            <div className={`text-center mb-6 ${
                                product.popular ? 'text-amber-700' : colors.text
                            }`}>
                              <div className="text-3xl font-bold mb-1">{product.credits}</div>
                              <div className="text-sm">AI Credits</div>
                            </div>

                            <div className="space-y-3">
                              {packageInfo.features.slice(0, 3).map((feature, index) => (
                                  <div key={index} className="flex items-center space-x-3">
                                    <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                                        product.popular
                                            ? 'bg-gradient-to-br from-yellow-500 via-amber-500 to-orange-500'
                                            : colors.icon
                                    }`}>
                                      <CheckIcon className="w-4 h-4 text-white"/>
                                    </div>
                                    <span className="text-gray-700 text-sm">{feature.text}</span>
                                  </div>
                              ))}
                            </div>
                          </div>

                          <div className="relative">
                            {product.popular ? (
                                <div className="relative group">
                                  <div
                                      className="absolute -inset-0.5 bg-gradient-to-r from-yellow-500 via-amber-500 to-orange-500 rounded-2xl blur opacity-75 group-hover:opacity-100 transition duration-300"></div>
                                  <Button
                                      variant="solid"
                                      className="relative w-full bg-gradient-to-r from-yellow-500 via-amber-500 to-orange-500 hover:from-yellow-600 hover:via-amber-600 hover:to-orange-600 text-white py-4 text-lg font-semibold rounded-2xl shadow-lg flex items-center justify-center"
                                      onClick={() => handleSelectProduct(product)}
                                      loading={selectingProduct === product.priceId}
                                  >
                                    {selectingProduct === product.priceId ? 'Processing...' : 'Purchase Credits'}
                                  </Button>
                                </div>
                            ) : (
                                <Button
                                    variant="solid"
                                    className={`w-full bg-gradient-to-r ${colors.gradient} hover:opacity-90 text-white py-4 text-lg font-semibold rounded-2xl flex items-center justify-center`}
                                    onClick={() => handleSelectProduct(product)}
                                    loading={selectingProduct === product.priceId}
                                >
                                  {selectingProduct === product.priceId ? 'Processing...' : 'Purchase Credits'}
                                </Button>
                            )}
                          </div>
                        </div>
                      </div>
                  );
                })}
              </div>
          ) : (
              <div className="text-center py-20 relative z-10">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-2xl mb-4">
                  <AlertCircle className="w-8 h-8 text-gray-400"/>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">No Credit Packages Available</h3>
                <p className="text-gray-600">
                  No purchase options are currently available. Please check back later.
                </p>
              </div>
          )}

          {/* Module Explanation Section - Below One-time Purchases */}
          <div id="module-explanation-onetime" className="max-w-4xl mx-auto mt-20 p-8 bg-gradient-to-r from-indigo-50 via-purple-50 to-blue-50 rounded-3xl border border-indigo-200 backdrop-blur-sm relative z-10">
            <div className="flex items-start space-x-6">
              <div className="flex-shrink-0">
                <div className="w-16 h-16 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-2xl flex items-center justify-center shadow-lg">
                  <Code className="w-8 h-8 text-white" />
                </div>
              </div>
              <div className="flex-1">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">What is a Module?</h3>
                <p className="text-gray-700 mb-6 text-lg leading-relaxed">
                  Each microservice is divided into <strong>modules</strong> - self-contained functional units that handle specific business logic. 
                  A typical microservice is composed of <strong>2 modules on average</strong>, but can range from 1 to several modules depending on complexity.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="flex items-center space-x-3">
                    <div className="w-3 h-3 bg-indigo-500 rounded-full"></div>
                    <span className="text-gray-700 font-medium">1 Module = 1 specific business domain</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-3 h-3 bg-purple-500 rounded-full"></div>
                    <span className="text-gray-700 font-medium">Complete with tests, API endpoints, and database models</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                    <span className="text-gray-700 font-medium">Ready for production deployment</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-3 h-3 bg-indigo-600 rounded-full"></div>
                    <span className="text-gray-700 font-medium">Fully documented and maintained</span>
                  </div>
                </div>
                
                <div className="mt-6 p-4 bg-white/70 rounded-xl border border-indigo-100">
                  <p className="text-gray-600 text-sm">
                    <strong>Example:</strong> An e-commerce microservice might have a "Product Management" module and an "Inventory Tracking" module, 
                    each handling their respective business logic with complete test coverage and API endpoints.
                  </p>
                </div>
              </div>
            </div>
          </div>

        </div>
    );
  };

  return (
      <DashboardLayout>
        <div className="max-w-7xl m-auto flex flex-col items-stretch gap-5 h-full w-full grow">
          <Card borderColor="[#dbe0f6]" bgColor="white" minHeight="full" grow>
            <div className="my-8 mx-4">
              {error && (
                  <Alert
                      showIcon
                      closable={true}
                      className="mb-6"
                      type="danger"
                  >
                    {error}
                  </Alert>
              )}

              {loading ? (
                  <div className="flex justify-center items-center py-20">
                    <div className="relative">
                      <div
                          className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-indigo-500"></div>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <img
                            src="/images/shreds-symbol.png"
                            alt="Shreds Symbol"
                            className="w-6 h-6 animate-pulse filter brightness-0 invert opacity-70"
                        />
                      </div>
                    </div>
                  </div>
              ) : (
                  renderContent()
              )}
            </div>
          </Card>
        </div>
      </DashboardLayout>
  );
};

export default PricingPlans;